// Findings #55: markers a supervisor needs to review crew data — "Crew" for a
// row captured in Crew Mode, "Corregido" for a row with at least one audited
// correction, and who logged it.

import React, { useEffect, useMemo, useState } from 'react'

import { api } from '../../utils/api'
import { useT } from '../../i18n'
import { actorLabel } from '../../utils/actorNames'
import type { CrewCorrectionResource } from './crewCorrectionFields'

const chipStyle = (background: string, color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  background,
  color,
  marginRight: 4,
  whiteSpace: 'nowrap'
})

type BadgeProps = Readonly<{
  captureSource?: string | null
  capturedByActorId?: string | null
  correctionCount?: number
  actorNames: Map<string, string> | null
  testId?: string
  /** Crew Mode Field Bundle v0 §3 item 8 flags from the record's metadata. */
  metadata?: Record<string, unknown> | null
}>

export function CrewRowBadges({ captureSource, capturedByActorId, correctionCount = 0, actorNames, testId, metadata }: BadgeProps): React.ReactNode {
  const t = useT()
  const isCrew = captureSource === 'crewMode'
  const who = actorLabel(actorNames, capturedByActorId)
  const late = metadata?.lateEntry === true
  const noMortality = metadata?.noMortality === true
  const notChecked = metadata?.notChecked === true
  if (!isCrew && correctionCount === 0 && !who && !late && !noMortality && !notChecked) return null
  return (
    <div style={{ marginTop: 2, lineHeight: 1.6 }} data-testid={testId}>
      {isCrew ? (
        <span style={chipStyle('#e0f2fe', '#075985')} title={t('crewCorrection.badge.crewTitle')}>
          {t('crewCorrection.badge.crew')}
        </span>
      ) : null}
      {noMortality ? (
        <span style={chipStyle('#dcfce7', '#166534')}>{t('crewCorrection.badge.noMortality')}</span>
      ) : null}
      {notChecked ? (
        <span style={chipStyle('#f1f5f9', '#334155')} title={t('crewCorrection.badge.notCheckedTitle')}>
          {t('crewCorrection.badge.notChecked')}
        </span>
      ) : null}
      {late ? (
        <span style={chipStyle('#ede9fe', '#5b21b6')}>{t('crewCorrection.badge.late')}</span>
      ) : null}
      {correctionCount > 0 ? (
        <span
          style={chipStyle('#fef3c7', '#92400e')}
          title={t('crewCorrection.badge.correctedTitle', { count: correctionCount })}
        >
          {t('crewCorrection.badge.corrected')}
        </span>
      ) : null}
      {who ? (
        <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
          {t('crewCorrection.badge.loggedBy', { name: who })}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Correction counts for the rows a table shows, in one request. `refreshKey`
 * re-fetches after a correction / delete. Unsynced ids simply get no count.
 */
export const useCorrectionCounts = (
  resource: CrewCorrectionResource,
  entityIds: string[],
  refreshKey: unknown = 0
): Map<string, number> => {
  const [counts, setCounts] = useState<Map<string, number>>(new Map())
  const idsKey = useMemo(() => Array.from(new Set(entityIds)).sort().join(','), [entityIds])

  useEffect(() => {
    let active = true
    const ids = idsKey ? idsKey.split(',') : []
    if (ids.length === 0) {
      setCounts(new Map())
      return undefined
    }
    void api.crewCorrections
      .summary(resource, ids)
      .then((rows) => {
        if (!active) return
        setCounts(new Map(rows.map((row) => [row.entityId, row.count])))
      })
      .catch(() => {
        // Offline or no permission: no badges rather than an error.
      })
    return () => {
      active = false
    }
  }, [resource, idsKey, refreshKey])

  return counts
}
